import { TIGER_RESOURCES } from "./tigerResourceData";
import { bootstrapTigerLang } from "./tigerTime";

let langOverrides: Record<string, string> = {};

/**
 * Port of C++ `get_tiger_resource`: returns the localized format string for
 * `id`. Precedence: language override → default table → the id itself (so a
 * missing resource is visible rather than blank).
 */
export function getTigerResource(id: string): string {
  if (Object.prototype.hasOwnProperty.call(langOverrides, id)) return langOverrides[id];
  if (Object.prototype.hasOwnProperty.call(TIGER_RESOURCES, id)) return TIGER_RESOURCES[id];
  return id;
}

/**
 * Port of the C++ lang-file loader. Each line is `id|value|`; a literal
 * backslash-n in the value becomes a newline and a single trailing `|` is
 * trimmed. Lines without a `|` are ignored (and logged).
 */
export function loadTigerResourceOverrides(fileText: string): void {
  for (const raw of fileText.split(/\r?\n/)) {
    if (raw.length === 0) continue;
    const line = raw.replace(/\\n/g, "\n");
    const bar = line.indexOf("|");
    if (bar === -1) {
      console.error(`No value associated with resource id ${line}`);
      continue;
    }
    const id = line.slice(0, bar);
    let value = line.slice(bar + 1);
    if (value.endsWith("|")) value = value.slice(0, -1);
    langOverrides[id] = value;
  }
}

/** Replace each successive `%s` in `template` with the next arg (C++ swprintf). */
export function tigerFormat(template: string, ...args: string[]): string {
  let i = 0;
  return template.replace(/%s/g, () => (i < args.length ? args[i++] : "%s"));
}

/**
 * Load the configured site/language resource overrides from the backend and
 * apply them, so subsequent `getTigerResource` calls return localized text.
 * Call this once per window BEFORE the first render (the resource table is read
 * synchronously during render, so late-loaded overrides wouldn't be picked up).
 * A no-op outside Tauri or when the backend reports no override file.
 */
export async function bootstrapTigerResources(): Promise<void> {
  // Load the locale date-format flag in BOTH environments.
  await bootstrapTigerLang();
  if (typeof window === "undefined") return;
  try {
    // Desktop reads the override file via the Tauri command; the web build fetches
    // the webserver's mirror route (without it the web UI would show the
    // compiled-in English defaults whatever TIGER_LANG says).
    let text: string | null;
    if ("__TAURI_INTERNALS__" in window) {
      const { invoke } = await import("@tauri-apps/api/core");
      text = await invoke<string | null>("get_tiger_resource_overrides");
    } else {
      text = await fetch("/api/get_tiger_resource_overrides").then((r) =>
        r.ok ? r.text() : null,
      );
    }
    if (text) loadTigerResourceOverrides(text);
  } catch (e) {
    console.error("bootstrapTigerResources failed:", e);
  }
}

/** Test-only: clear language overrides between tests. */
export function __resetTigerResourceOverridesForTest(): void {
  langOverrides = {};
}
